import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  type SegmentedControlOption,
  Select,
  type SelectOption,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { WEEKDAY_KEYS } from '~/components/common/DayGrid'
import {
  CollapsibleFilterToolbar,
  DateStepper,
  FilterGrid,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { convertAreaLabel } from '~/models/area'
import { formatDateDisplay } from '~/models/dateTime'
import { TargetDateSegment } from '~/pages/DemandForecastList/components/TargetDateSegment'
import type { TargetDateSegmentKey } from '~/pages/DemandForecastList/models/targetDateSegment'

/**
 * エリア select の「全エリア」option を表す sentinel 値。 業務エリア (AreaCode enum
 * 値の文字列表現、`'1'`〜`'10'`) と衝突しない文字列を使う (design-note
 * demand-list-area-filter Decision #2: 「全エリア」を明示 option とする)。
 */
const AREA_FILTER_ALL_VALUE = 'ALL'

/** 開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。 */
const FILTER_STORAGE_KEY = 'demandForecastList'

export type FilterBarProps = {
  /** 現在選択中の対象日 */
  targetDate: Date
  /**
   * エリア select の候補 (design-note demand-list-area-filter Decision #1:
   * 個人設定の業務エリア。 10 エリア北→南順 ∩ 業務エリア、 呼び出し側で導出済み)。
   */
  businessAreas: AreaCode[]
  /** 選択中のエリア (undefined = 全エリア)。 単一選択 (Decision #2)。 */
  selectedArea: AreaCode | undefined
  /** 選択中のステータス (undefined = 全て)。WorkflowCaseStatus で絞り込む。 */
  selectedStatus: WorkflowCaseStatus | undefined
  onTargetDateChange: (date: Date) => void
  /** エリア select の選択変更 (undefined = 「全エリア」選択) */
  onAreaChange: (area: AreaCode | undefined) => void
  onStatusChange: (status: WorkflowCaseStatus | undefined) => void
  /**
   * 対象日 radio (当日/翌日/翌々日/翌々日以降) の現在アクティブな区分。
   * date picker / `<` `>` で選んだ日が該当区分に一致しない場合 (過去日等) は
   * undefined (= 全ボタン非 active)。
   */
  targetDateSegment: TargetDateSegmentKey | undefined
  /** 対象日 radio クリック。対象日の算出は呼び出し側 (操作時点の現在日) に委ねる */
  onTargetDateSegmentChange: (segment: TargetDateSegmentKey) => void
}

export const FilterBar = ({
  targetDate,
  businessAreas,
  selectedArea,
  selectedStatus,
  onTargetDateChange,
  onAreaChange,
  onStatusChange,
  targetDateSegment,
  onTargetDateSegmentChange,
}: FilterBarProps): React.ReactElement => {
  const { t } = useTranslation()

  // spec §7 / design #dfl-status-seg: すべて / 未着手 / 進行中 / 完了 / 削除
  // 実績反映済は #4097 で P0 スコープ外と確定したため含めない
  const statusFilterOptions: Array<SegmentedControlOption<string>> = useMemo(
    () => [
      { value: '', label: t('page:demandForecastList.filter.statusAll') },
      {
        value: String(WorkflowCaseStatus.NOT_STARTED),
        label: t('page:workflowCommon.caseStatus.notStarted'),
      },
      {
        value: String(WorkflowCaseStatus.IN_PROGRESS),
        label: t('page:workflowCommon.caseStatus.inProgress'),
      },
      {
        value: String(WorkflowCaseStatus.COMPLETE),
        label: t('page:workflowCommon.caseStatus.complete'),
      },
      {
        value: String(WorkflowCaseStatus.DELETED),
        label: t('page:workflowCommon.caseStatus.deleted'),
      },
    ],
    [t],
  )

  // convertAreaLabel は i18next.t を引くため、 言語切替 (I18nProvider が session RPC 後に
  // changeLanguage する) に追従できるよう依存に t を入れる (調達計画一覧と同形)。
  // 候補 = 「全エリア」+ 業務エリア (北→南順、 呼び出し側で導出済み。 Decision #1)。
  const areaSelectOptions: SelectOption[] = useMemo(
    () => [
      {
        value: AREA_FILTER_ALL_VALUE,
        label: t('page:demandForecastList.filter.areaAll'),
      },
      ...businessAreas.map((areaCode) => ({
        value: String(areaCode),
        label: convertAreaLabel(areaCode),
      })),
    ],
    [t, businessAreas],
  )
  const selectedAreaValue =
    selectedArea === undefined ? AREA_FILTER_ALL_VALUE : String(selectedArea)
  const selectedAreaOption =
    areaSelectOptions.find((opt) => opt.value === selectedAreaValue) ?? null

  // 開閉式のフィルター。数える・戻すのはエリア・ステータス。対象日は見るデータの日を替える期間関連なので
  // 数えず、クリアでも戻さない (UI 辞書 toolbar-collapsible.md 計画一覧の「対象日はクリアしない・件数に数えない」)
  const all = t('common:filter.all')
  const targetDateLabel = t('page:demandForecastList.filter.targetDate')
  const statusLabel = t('page:demandForecastList.filter.status')
  const areaLabel = t('page:demandForecastList.filter.area')
  const modifiedCount = [
    selectedArea !== undefined,
    selectedStatus !== undefined,
  ].filter(Boolean).length
  const clearFilters = (): void => {
    onAreaChange(undefined)
    onStatusChange(undefined)
  }
  // 要約の対象日は日付欄の閉じた表示と同じ `YYYY/MM/DD (曜)`
  const summary: ToolbarStatusItem[] = [
    {
      key: 'area',
      label: areaLabel,
      value: selectedArea === undefined ? all : convertAreaLabel(selectedArea),
    },
    {
      key: 'targetDate',
      label: targetDateLabel,
      value: t('common:date.withWeekday', {
        date: formatDateDisplay(targetDate),
        weekday: t(
          `common:date.calendar.weekday.${WEEKDAY_KEYS[targetDate.getDay()]}`,
        ),
      }),
    },
    {
      key: 'status',
      label: statusLabel,
      value:
        selectedStatus === undefined
          ? all
          : (statusFilterOptions.find(
              (option) => option.value === String(selectedStatus),
            )?.label ?? all),
    },
  ]

  return (
    <CollapsibleFilterToolbar
      modifiedCount={modifiedCount}
      onClear={clearFilters}
      storageKey={FILTER_STORAGE_KEY}
      summary={summary}
    >
      <FilterGrid
        items={[
          {
            key: 'targetDate',
            label: targetDateLabel,
            control: (
              <Flex alignItems="center" flexWrap="wrap" gap={2}>
                <DateStepper
                  dateAriaLabel={targetDateLabel}
                  hideTodayButton
                  onChange={onTargetDateChange}
                  value={targetDate}
                />
                <TargetDateSegment
                  onChange={onTargetDateSegmentChange}
                  value={targetDateSegment}
                />
              </Flex>
            ),
          },
          {
            // design `#dfl-status-seg` は select ではなく segmented
            // (`design/demand/forecast-list.html`「2026-07-15: select → DS segmented 化」)。
            key: 'status',
            label: statusLabel,
            control: (
              <AccessibleSegmentedControl
                aria-label={statusLabel}
                name="demand-forecast-status"
                onSelect={(next) => {
                  if (next === '') {
                    onStatusChange(undefined)
                    return
                  }
                  const num = Number(next)
                  if (Number.isNaN(num)) {
                    onStatusChange(undefined)
                    return
                  }
                  onStatusChange(num as WorkflowCaseStatus)
                }}
                options={statusFilterOptions}
                selected={
                  selectedStatus === undefined ? '' : String(selectedStatus)
                }
              />
            ),
          },
          {
            // design `#dfl-area-ms` は複数選択 (EclMultiSelect) だが spec / mock-app は
            // 単一選択 (design gap、design-note demand-list-area-filter Decision #2)。
            // 候補は個人設定の業務エリアに連動 (Decision #1)。
            key: 'area',
            label: areaLabel,
            control: (
              <Box minWidth="200px" css={selectFontFix}>
                <Select
                  aria-label={areaLabel}
                  isMulti={false}
                  // 一覧の sticky TH に menu が覆われるため body へ portal する。
                  menuPortalTarget={document.body}
                  onChange={(opt) => {
                    if (opt === null) return
                    onAreaChange(
                      opt.value === AREA_FILTER_ALL_VALUE
                        ? undefined
                        : (Number(opt.value) as AreaCode),
                    )
                  }}
                  options={areaSelectOptions}
                  selectedOptions={selectedAreaOption}
                  size="md"
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
                />
              </Box>
            ),
          },
        ]}
      />
    </CollapsibleFilterToolbar>
  )
}
